import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Box, Button, Tooltip, Typography } from "@mui/material";
import { ColDef, ICellRendererParams } from "ag-grid-enterprise";
import { ReactNode } from "react";

import { WeldTest } from "@/shared/models/WeldTest";

type Props = {
	onShowPhoto: (title: string, path: string) => void;
	onEdit: (test: WeldTest) => void;
	onDelete: (test: WeldTest) => void;
};

const CenteredCell = ({ children }: { children: ReactNode }) => (
	<Box
		sx={{
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			height: "100%",
		}}
	>
		{children}
	</Box>
);

const PassFailCell = (params: ICellRendererParams<WeldTest>) => (
	<CenteredCell>
		{params.value ? (
			<CheckIcon fontSize="small" color="success" />
		) : (
			<CloseIcon fontSize="small" color="error" />
		)}
	</CenteredCell>
);

export const useTableData = ({ onShowPhoto, onEdit, onDelete }: Props) => {
	const photoCell =
		(title: string, field: "Photo1Path" | "Photo2Path") =>
		(params: ICellRendererParams<WeldTest>) => {
			const path = params.data?.[field];
			if (!path)
				return (
					<CenteredCell>
						<Typography variant="caption" color="text.disabled">
							No Photo
						</Typography>
					</CenteredCell>
				);
			return (
				<CenteredCell>
					<Button
						size="small"
						variant="contained"
						sx={{ textTransform: "none", py: 0 }}
						onClick={() => onShowPhoto(title, path)}
					>
						View
					</Button>
				</CenteredCell>
			);
		};

	const columnDefs: ColDef[] = [
		{
			field: "AssetName",
			headerName: "Line",
			flex: 1,
			minWidth: 90,
			maxWidth: 110,
		},
		{
			field: "CreatedDate",
			headerName: "Timestamp",
			valueGetter: (params) =>
				dxtToLocalServerTime(
					params.data?.CreatedDate,
					"HH:mm:ss - MM/dd/yyyy",
				) || "",
			flex: 2,
			minWidth: 160,
		},
		{
			field: "PartNumber",
			headerName: "Part Number",
			wrapHeaderText: true,
			flex: 1.5,
			minWidth: 100,
		},
		{
			field: "ExtendedPartNumber",
			headerName: "Extended Part Number",
			wrapHeaderText: true,
			flex: 1.5,
			minWidth: 120,
		},
		{
			field: "BendTest1Passed",
			headerName: "Bend Test 1",
			wrapHeaderText: true,
			cellRenderer: PassFailCell,
			minWidth: 80,
			maxWidth: 100,
		},
		{
			field: "BendTest2Passed",
			headerName: "Bend Test 2",
			wrapHeaderText: true,
			cellRenderer: PassFailCell,
			minWidth: 80,
			maxWidth: 100,
		},
		{
			field: "Photo1Path",
			headerName: "Bend Test 1 Photo",
			wrapHeaderText: true,
			cellRenderer: photoCell("Bend Test 1 Photo", "Photo1Path"),
			sortable: false,
			minWidth: 95,
			maxWidth: 110,
		},
		{
			field: "Photo2Path",
			headerName: "Bend Test 2 Photo",
			wrapHeaderText: true,
			cellRenderer: photoCell("Bend Test 2 Photo", "Photo2Path"),
			sortable: false,
			minWidth: 95,
			maxWidth: 110,
		},
		{
			field: "Operator",
			headerName: "Employee #",
			flex: 1,
			minWidth: 90,
			// Fallback for tests older than the operator field
			valueGetter: (params) => params.data?.Operator ?? params.data?.CreatedBy,
		},
		{
			field: "Notes",
			headerName: "Comments",
			flex: 3,
			cellStyle: {
				justifyContent: "flex-start",
				textAlign: "left",
			},
			wrapText: true,
			autoHeight: true,
			minWidth: 140,
		},
		{
			colId: "actions",
			headerName: "",
			minWidth: 100,
			maxWidth: 100,
			sortable: false,
			filter: false,
			cellStyle: { padding: 0 },
			cellRenderer: (params: ICellRendererParams<WeldTest>) => {
				const test = params.data;
				if (!test) return null;
				const btnSx = { minWidth: 28, padding: "4px" };
				return (
					<CenteredCell>
						<Tooltip title="Edit this test">
							<Button size="small" sx={btnSx} onClick={() => onEdit(test)}>
								<EditIcon color="primary" />
							</Button>
						</Tooltip>
						<Tooltip title="Delete this test">
							<Button size="small" sx={btnSx} onClick={() => onDelete(test)}>
								<DeleteIcon color="secondary" />
							</Button>
						</Tooltip>
					</CenteredCell>
				);
			},
		},
	];

	return { columnDefs };
};
